import { Box, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import type { FC } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { auth, isErrorWithCode } from "../auth/auth";
import AsyncLoadingButton from "../components/asyncLoadingButton/AsyncLoadingButton";
import FormTextField, { emailValidationPattern } from "../components/formComponents/FormTextField";
import ReturnToPasswordLoginButton from "./ReturnToPasswordLoginButton";
import SignInSignUpContainer from "./SignInSignUpContainer";

interface ResetPasswordFormState {
  readonly email: string;
}

function getResetPasswordErrorDetails(error: unknown): string {
  if (!error) {
    return "nd.";
  }
  if (isErrorWithCode(error) && error.code === "auth/user-not-found") {
    return "Użytkownik z takim adresem email nie istnieje";
  }
  if (typeof error === "object" && "message" in error && typeof error.message === "string") {
    return error.message;
  }
  return JSON.stringify(error);
}

const ResetPassword: FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { control, handleSubmit } = useForm<ResetPasswordFormState>({
    defaultValues: { email: "" },
  });
  const navigate = useNavigate();

  const onSubmit = async (email: string) => {
    try {
      await auth.sendPasswordResetEmail(email);
      enqueueSnackbar({
        variant: "success",
        message: `Wiadomość email została wysłana. Sprawdź swoją skrzynkę pocztową.`,
      });
    } catch (error) {
      // auth/user-not-found
      const reason = getResetPasswordErrorDetails(error);
      enqueueSnackbar({
        variant: "error",
        message: `Nie udało się wysłać wiadomości email. Powód: ${reason}`,
      });
    }
  };

  return (
    <SignInSignUpContainer minWidth="550px">
      <>
        <Typography
          variant="h4"
          gutterBottom
          color="text.primary"
          sx={{
            fontWeight: "bold",
          }}
        >
          Odzyskaj dostęp do konta
        </Typography>
        <FormTextField
          margin="normal"
          required
          fullWidth
          id="email"
          label="Adres e-mail"
          name="email"
          autoComplete="email"
          autoFocus
          type="email"
          variant="outlined"
          control={control}
          rules={{ pattern: emailValidationPattern }}
          InputProps={{ sx: { borderRadius: 16 } }}
        />
        <Box sx={{ mt: 1, mb: 1 }} />
        <AsyncLoadingButton
          onClick={handleSubmit((s) => onSubmit(s.email))}
          type="submit"
          color="primary"
          fullWidth
          variant="contained"
          size="large"
          sx={{ py: 1.5, mb: 2, fontSize: "1.1rem", borderRadius: 16, boxShadow: "0 4px 12px rgba(53, 87, 161, 0.3)" }}
        >
          Wyślij email
        </AsyncLoadingButton>
        <ReturnToPasswordLoginButton onClick={() => navigate("/")} />
      </>
    </SignInSignUpContainer>
  );
};

export default ResetPassword;
